import { WorldEventViewSchema } from '../../ws/protocol';
import { z } from 'zod';
import { ErrorCodeSchema } from '../../errors';
import { AccountIdSchema, OperationIdSchema, PlayerIdSchema, SessionIdSchema } from '../../ids';
import { WorldLayoutIdSchema } from '../../models/place';
import { PlayerSchema } from '../../models/player';
import { ItemAmountsSchema, ItemIdSchema } from '../../models/items';
import { TimestampMsSchema } from '../../time';
import { GOD_MAX_SPEED } from '../../ws/protocol';
import { DEV_PREFIX, defineEndpoint } from '../endpoint';

export const devGetWorld = defineEndpoint({
  method: 'GET',
  path: `${DEV_PREFIX}/world`,
  access: 'dev',
  response: z.object({
    serverTime: TimestampMsSchema,
    /** Offset applied to the game clock by the time-advance tool. */
    clockOffsetMs: z.number().int(),
    players: z.array(PlayerSchema),
    activeSessions: z.array(
      z.object({
        id: SessionIdSchema,
        accountId: AccountIdSchema,
        playerId: PlayerIdSchema.nullable(),
        createdAt: TimestampMsSchema,
        expiresAt: TimestampMsSchema,
        connected: z.boolean(),
      }),
    ),
    /**
     * Where each character is. Online characters report their live in-memory position; offline
     * ones their last persisted position.
     */
    characters: z.array(
      z.object({
        playerId: PlayerIdSchema,
        /** World position, in metres. */
        x: z.number(),
        y: z.number(),
        online: z.boolean(),
        /** The weapon an online character wears, live (null: bare hands). */
        weapon: ItemIdSchema.nullable().optional(),
      }),
    ),
  }),
});

/** Drops all game data and loads the seed again. Every open session is closed. */
export const devResetWorld = defineEndpoint({
  method: 'POST',
  path: `${DEV_PREFIX}/world/reset`,
  access: 'dev',
  body: z.object({ seed: z.string().min(1).max(64).optional() }),
  response: z.object({ seed: z.string(), players: z.number().int().nonnegative() }),
});

/**
 * Simulates a network drop for a player's realtime connection (non-terminal close code), so
 * reconnection can be tested by hand, by e2e tests and by bot scenarios.
 */
export const devDropConnection = defineEndpoint({
  method: 'POST',
  path: `${DEV_PREFIX}/realtime/drop`,
  access: 'dev',
  body: z.object({ playerId: PlayerIdSchema }),
  response: z.object({ dropped: z.boolean() }),
});

const PercentilesSchema = z.object({ p50: z.number(), p99: z.number(), max: z.number() });

/**
 * Runtime health of the realtime server since the previous call (each call starts a new
 * measurement window). Used by load tests and for diagnosing lag.
 */
export const devGetMetrics = defineEndpoint({
  method: 'GET',
  path: `${DEV_PREFIX}/metrics`,
  access: 'dev',
  response: z.object({
    stepMs: PercentilesSchema,
    eventLoopDelayMs: PercentilesSchema,
    cpuPercent: z.number(),
    bytesSentPerSecond: z.number(),
    messagesSentPerSecond: z.number(),
    heapUsedMb: z.number(),
    rssMb: z.number(),
    httpRequestsPerSecond: z.number(),
    httpMs: PercentilesSchema,
    droppedSteps: z.number().int().nonnegative(),
    persistedRowsPerSecond: z.number(),
    writeMs: PercentilesSchema,
    /** Characters handed over between shards per second. */
    handOversPerSecond: z.number(),
    connections: z.number().int().nonnegative(),
    /**
     * The single scheduler (CORE-SCHEDULER): events run and failed since startup, and how late
     * (game ms) the last one ran after falling due.
     */
    scheduler: z.object({
      executed: z.number().int().nonnegative(),
      failed: z.number().int().nonnegative(),
      lastLagMs: z.number(),
    }),
    /** Characters online per active scene. */
    scenes: z.record(z.string(), z.number().int().nonnegative()),
    /** Simulation shards (threads): share of time busy, live places and characters. */
    shards: z.array(
      z.object({
        index: z.number().int().nonnegative(),
        utilization: z.number(),
        places: z.number().int().nonnegative(),
        characters: z.number().int().nonnegative(),
        droppedSteps: z.number().int().nonnegative(),
        /** Chunks compiled in the shard's memory, and replicas of its neighbors' entities. */
        chunks: z.number().int().nonnegative(),
        replicas: z.number().int().nonnegative(),
        /** Living enemies: custodians of places and wild ones (CREATURE-SPAWN). */
        enemies: z.object({
          custody: z.number().int().nonnegative(),
          wild: z.number().int().nonnegative(),
        }),
        /** Heap used by the shard's thread (MB). */
        heapMb: z.number().nonnegative(),
        /** Chunks compiled per second (each one runs inside the step). */
        compilesPerSecond: z.number().nonnegative(),
        /**
         * Combat counters since the shard started (COMBAT-TTK): damage dealt by weapons and by
         * techniques (enemies' blows included) and taken by characters, parries, guarded blows,
         * dodges, Focus spent, ms under control and defeats (of characters and enemies).
         */
        combat: z.object({
          dealtWeapon: z.number().nonnegative(),
          dealtTechnique: z.number().nonnegative(),
          taken: z.number().nonnegative(),
          parried: z.number().int().nonnegative(),
          guarded: z.number().int().nonnegative(),
          dodges: z.number().int().nonnegative(),
          focusSpent: z.number().nonnegative(),
          controlledMs: z.number().nonnegative(),
          kills: z.number().int().nonnegative(),
        }),
      }),
    ),
    /** Gateway processes (players' sockets): load and traffic over the last second. */
    gateways: z.array(
      z.object({
        id: z.string(),
        pid: z.number().int().nonnegative(),
        connections: z.number().int().nonnegative(),
        bytesSent: z.number().nonnegative(),
        messagesSent: z.number().nonnegative(),
        eventLoopP99Ms: z.number(),
        cpuPercent: z.number(),
      }),
    ),
    /**
     * What the townspeople told since startup (NPC-RUMOR §8): rumors written by class, talks
     * served, lines told, and talks refused for standing too far.
     */
    rumors: z.object({
      written: z.record(z.string(), z.number().int().nonnegative()),
      talks: z.number().int().nonnegative(),
      lines: z.number().int().nonnegative(),
      refusedFar: z.number().int().nonnegative(),
    }),
  }),
});

/**
 * The rumors alive now (NPC-RUMOR §8): how many each region keeps, and how long each class is
 * remembered (its weight's life).
 */
export const devGetRumors = defineEndpoint({
  method: 'GET',
  path: `${DEV_PREFIX}/rumors`,
  access: 'dev',
  response: z.object({
    live: z.record(z.string(), z.number().int().nonnegative()),
    lifeMs: z.record(z.string(), z.number().nonnegative()),
  }),
});

const Count = z.number().int().nonnegative();

/**
 * How the quests are played (QUEST §Métricas): each chain's characters on it and done, where it is
 * left (by step) and how long it took those who ended it; each issuer's board contracts by status,
 * how many were completed and the coins they paid (a power's id, or `players` for characters').
 */
export const devGetQuests = defineEndpoint({
  method: 'GET',
  path: `${DEV_PREFIX}/quests`,
  access: 'dev',
  response: z.object({
    chains: z.record(
      z.string(),
      z.object({
        active: Count,
        done: Count,
        abandoned: z.record(z.string(), Count),
        meanDoneMs: z.number().nonnegative().nullable(),
      }),
    ),
    contracts: z.record(
      z.string(),
      z.object({
        open: Count,
        closed: Count,
        expired: Count,
        completions: Count,
        coinsPaid: Count,
      }),
    ),
  }),
});

/**
 * How construction and homes are played (BUILD and HOME §Métricas): each definition's projects
 * (placed, completed, cancelled, abandoned; its modules' counted on it) and the mean time a
 * structure took; the modules installed by structure; lots by region, kind and standing; locks
 * forced (standing now, ever) and inn rooms forced; storages robbed (operations); squats fallen;
 * rooms rented and the homes characters chose to reappear at.
 */
export const devGetBuilding = defineEndpoint({
  method: 'GET',
  path: `${DEV_PREFIX}/building`,
  access: 'dev',
  response: z.object({
    projects: z.record(
      z.string(),
      z.object({
        placed: Count,
        completed: Count,
        cancelled: Count,
        abandoned: Count,
        meanMs: z.number().nonnegative().nullable(),
      }),
    ),
    modules: z.record(z.string(), z.record(z.string(), Count)),
    lots: z.array(
      z.object({ region: z.string(), kind: z.string(), status: z.string(), count: Count }),
    ),
    forced: z.object({ standing: Count, locks: Count, rooms: Count }),
    thefts: Count,
    squatsFallen: Count,
    rooms: z.object({ paid: Count, expired: Count, safe: Count }),
    homes: z.object({ hearth: Count, room: Count }),
  }),
});

/**
 * The powers' immune system at a glance (NPC-KINGDOM-REACT, its metrics): the standing coalitions
 * with their faction's threat, and how many ended each way.
 */
export const devGetReaction = defineEndpoint({
  method: 'GET',
  path: `${DEV_PREFIX}/reaction`,
  access: 'dev',
  response: z.object({
    standing: z.array(
      z.object({
        id: z.string(),
        target: z.object({ kind: z.enum(['company', 'player']), id: z.string() }),
        step: z.string(),
        powers: z.array(z.string()),
        threat: z.number().nonnegative(),
        losses: Count,
        formedAt: z.number().int(),
      }),
    ),
    ended: z.record(z.string(), Count),
  }),
});

/** Records a V8 CPU profile of the running server for `seconds` and saves it under data/. */
/**
 * Saves a client's combat trace (a temporary diagnostic, 2026-10-09) next to the server's
 * (`data/traces`): its lines, as the client recorded them.
 */
export const devSaveTrace = defineEndpoint({
  method: 'POST',
  path: `${DEV_PREFIX}/trace`,
  access: 'dev',
  body: z.object({
    name: z.string().regex(/^[a-zA-Z0-9_-]{1,40}$/),
    /** One part of it: the server appends each part to the same file. */
    lines: z.array(z.record(z.string(), z.unknown())).max(200),
  }),
  response: z.object({ file: z.string() }),
});

export const devCaptureProfile = defineEndpoint({
  method: 'POST',
  path: `${DEV_PREFIX}/profile`,
  access: 'dev',
  body: z.object({ seconds: z.number().int().min(1).max(120) }),
  response: z.object({ file: z.string() }),
});

/**
 * A persisted game event (PrimeraVersion.md §12 > Eventos): who did what to which target, when
 * (server game time) and through which operation. The log explains outcomes and lets tests
 * prove that no resources were duplicated.
 */
export const GameEventSchema = z.object({
  id: z.string(),
  type: z.string(),
  playerId: PlayerIdSchema.nullable(),
  /** What the event acted on, as `<kind>:<id>` (e.g. `building:<uuid>`). */
  target: z.string().nullable(),
  operationId: OperationIdSchema.nullable(),
  data: z.record(z.string(), z.unknown()),
  createdAt: TimestampMsSchema,
});
export type GameEvent = z.infer<typeof GameEventSchema>;

/** Most recent events first, optionally for one player. */
export const devListEvents = defineEndpoint({
  method: 'GET',
  path: `${DEV_PREFIX}/events`,
  access: 'dev',
  query: z.object({
    playerId: PlayerIdSchema.optional(),
    /** Only events of this type (e.g. `market.offer_sold`). */
    type: z.string().min(1).max(64).optional(),
    /** Only the events of one command (its operation id). */
    operationId: OperationIdSchema.optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
  }),
  response: z.object({ events: z.array(GameEventSchema) }),
});

/**
 * Commands the server refused and errors it answered, most recent first (PrimeraVersion.md §15:
 * "logs de comandos rechazados y errores con identificadores correlacionables"). HTTP ones carry
 * the `x-request-id` the client also received; realtime ones the `req` they answered.
 */
export const RejectionSchema = z.discriminatedUnion('source', [
  z.object({
    source: z.literal('http'),
    at: TimestampMsSchema,
    playerId: PlayerIdSchema.nullable(),
    code: ErrorCodeSchema,
    message: z.string(),
    requestId: z.string(),
    route: z.string(),
  }),
  z.object({
    source: z.literal('ws'),
    at: TimestampMsSchema,
    playerId: PlayerIdSchema.nullable(),
    code: ErrorCodeSchema,
    message: z.string(),
    req: z.number().int().nullable(),
  }),
]);
export type Rejection = z.infer<typeof RejectionSchema>;

export const devListRejections = defineEndpoint({
  method: 'GET',
  path: `${DEV_PREFIX}/rejections`,
  access: 'dev',
  query: z.object({
    limit: z.coerce.number().int().min(1).max(200).default(100),
  }),
  response: z.object({ rejections: z.array(RejectionSchema) }),
});

/**
 * Seed scenarios a world reset can load (the dev panel's scenario picker), each with its theme's
 * group (B152): its name and what it does are the catalogs' `dev.scenarios.catalog.<name>`, the
 * group's `dev.scenarios.groups.<group>`.
 */
export const devListScenarios = defineEndpoint({
  method: 'GET',
  path: `${DEV_PREFIX}/scenarios`,
  access: 'dev',
  response: z.object({
    scenarios: z.array(
      z.object({
        name: z.string(),
        players: z.number().int().nonnegative(),
        group: z.string(),
        /** Where its action happens (world metres, B131): «watch in god mode» goes there. */
        watch: z.object({ x: z.number(), y: z.number() }).nullable(),
        /** Whether its watcher's god mode stays perceived (`devSetGod.perceived`). */
        watchPerceived: z.boolean(),
        /** The world it is written for. */
        layout: WorldLayoutIdSchema,
        /** Whether it can stand on the world this server runs: a reset to one that cannot is refused. */
        runsHere: z.boolean(),
      }),
    ),
    /** The scenario a reset loads when none is given. */
    defaultScenario: z.string(),
    /** The world layout this server runs. */
    layout: WorldLayoutIdSchema,
  }),
});

/**
 * Advances the game clock (PrimeraVersion.md §15): production and persistent timers (node
 * respawns, protections) see the time pass; live movement and combat do not. Persisted.
 */
/**
 * Silences a character in the chat (SOC-CHAT, OPS-MODERATION) for `ms` (0 lifts it), until there
 * is administration. It keeps playing; its lines are refused with `chat_muted`.
 */
export const devMuteChat = defineEndpoint({
  method: 'POST',
  path: `${DEV_PREFIX}/chat/mute`,
  access: 'dev',
  body: z.object({
    playerId: PlayerIdSchema,
    ms: z
      .number()
      .int()
      .nonnegative()
      .max(365 * 24 * 3_600_000),
    reason: z.string().max(200).default(''),
  }),
  response: z.object({ until: z.number().int().nonnegative() }),
});

/** The latest chat reports (OPS-MODERATION), newest first, with the lines before each. */
export const devChatReports = defineEndpoint({
  method: 'GET',
  path: `${DEV_PREFIX}/chat/reports`,
  access: 'dev',
  response: z.object({
    reports: z.array(
      z.object({
        id: z.string(),
        reporterId: PlayerIdSchema,
        speakerId: PlayerIdSchema,
        channel: z.string(),
        line: z.string(),
        context: z.array(
          z.object({
            id: z.number().int(),
            at: z.number().int(),
            speaker: PlayerIdSchema,
            text: z.string(),
          }),
        ),
        reason: z.string(),
        at: z.number().int(),
      }),
    ),
  }),
});

/**
 * Opens a world event at once (WORLD-EVENT, a dev tool to try it): a class at a free site of a
 * region, whatever its phase, table or cap; it runs its stages like any. Answers it, or null when
 * the region has no free site of that class.
 */
export const devOpenWorldEvent = defineEndpoint({
  method: 'POST',
  path: `${DEV_PREFIX}/world-events`,
  access: 'dev',
  body: z.object({
    region: z.string().min(1).max(64),
    class: z.string().min(1).max(32),
  }),
  response: z.object({ event: WorldEventViewSchema.nullable() }),
});

/**
 * The sky from the dev tools (B113): jump to the start of a moment of the day, force a region's
 * weather (`null` gives it back its seeded weather), or both. The day moves by its own origin,
 * never by the game clock, so production and timers do not jump with it. Kept in memory: a
 * restart gives the configured sky back. Answers the sky as it is now.
 */
export const devSetSky = defineEndpoint({
  method: 'POST',
  path: `${DEV_PREFIX}/sky`,
  access: 'dev',
  body: z.object({
    phase: z.enum(['dawn', 'day', 'dusk', 'night']).optional(),
    weather: z
      .object({ region: z.string().min(1).max(64), kind: z.string().min(1).max(32).nullable() })
      .optional(),
    /**
     * Forces a region's Pulse phase (plan H1), `null` giving it back its cycle; `major` forces its
     * Major Pulse (H2c), `false` giving it back its week.
     */
    pulse: z
      .object({
        region: z.string().min(1).max(64),
        phase: z.enum(['calm', 'agitation', 'intense']).nullable().optional(),
        major: z.boolean().optional(),
      })
      .optional(),
  }),
  response: z.object({
    /** The moment of the day now, and how long until the next. */
    phase: z.enum(['dawn', 'day', 'dusk', 'night']),
    nextInMs: z.number().int().nonnegative(),
    /** Regions whose weather is forced, by id. */
    forced: z.record(z.string(), z.string()),
    /** Regions whose Pulse phase is forced, by id. */
    pulse: z.record(z.string(), z.enum(['calm', 'agitation', 'intense'])),
    /** Regions whose Major Pulse is forced (H2c). */
    major: z.array(z.string()).default([]),
  }),
});

export const devAdvanceClock = defineEndpoint({
  method: 'POST',
  path: `${DEV_PREFIX}/clock/advance`,
  access: 'dev',
  body: z.object({
    ms: z
      .number()
      .int()
      .positive()
      .max(30 * 24 * 60 * 60 * 1000),
  }),
  response: z.object({ offsetMs: z.number().int(), serverTime: TimestampMsSchema }),
});

const HoldingsSchema = z.object({ items: ItemAmountsSchema, coins: z.number() });

/**
 * Conservation audit (PrimeraVersion.md §18): what players hold now against the seeded baseline
 * replayed through the event log, plus integrity anomalies. `ok` when everything adds up.
 */
export const devAuditWorld = defineEndpoint({
  method: 'GET',
  path: `${DEV_PREFIX}/audit`,
  access: 'dev',
  response: z.object({
    baseline: HoldingsSchema.nullable(),
    expected: HoldingsSchema.nullable(),
    actual: HoldingsSchema,
    unclaimedStock: ItemAmountsSchema,
    anomalies: z.array(z.string()),
    events: z.number().int().nonnegative(),
    ok: z.boolean(),
  }),
});

/**
 * The map's wealth around a point (RES-DISTRIBUTION, D1): the deposits that reach the square of
 * `radius` metres around it, and its fields there. Dev tools only: players learn it by
 * prospecting (D3), never from the server's seed.
 */
export const devGetResources = defineEndpoint({
  method: 'GET',
  path: `${DEV_PREFIX}/resources`,
  access: 'dev',
  query: z.object({
    x: z.coerce.number().int(),
    y: z.coerce.number().int(),
    radius: z.coerce.number().int().min(1).max(2_000).default(200),
  }),
  response: z.object({
    deposits: z.array(
      z.object({
        id: z.string(),
        material: ItemIdSchema,
        x: z.number(),
        y: z.number(),
        radius: z.number(),
        grade: z.number(),
        reserve: z.number(),
        written: z.boolean(),
      }),
    ),
    fields: z.object({ wood: z.number(), fertility: z.number() }),
  }),
});

/**
 * A world coordinate in metres: the surface (the 64 km world and some margin) and the undercroft,
 * ~102 km off the origin and as wide as The Hollow and its dungeons (DEPTH, ADR 0069).
 */
const WorldMetreSchema = z.number().min(-131_000).max(131_000);

/**
 * Moves a connected character to a world point at once (B131, the dev tools' god mode), as a
 * respawn moves it: a `world` message and a new input stream. It lands on the nearest free ground
 * around the point (exactly there in god mode), across shards if need be. `moved` is false when
 * the character is not in the world (or is between shards: ask again).
 */
export const devTeleport = defineEndpoint({
  method: 'POST',
  path: `${DEV_PREFIX}/teleport`,
  access: 'dev',
  body: z.object({ playerId: PlayerIdSchema, x: WorldMetreSchema, y: WorldMetreSchema }),
  response: z.object({ moved: z.boolean() }),
});

/**
 * God mode for a connected character (B131): it goes through everything at its pace times
 * `speed`, takes no damage and no creature perceives it. Kept in memory with the character (a
 * hand-over carries it, a server restart ends it); its owner hears `god`.
 */
/**
 * Seeded characters nobody plays stand in the world (B131, a scenario watched): each joins like a
 * player through a channel that sends nowhere, the same rules on it (perceived, struck, felled,
 * respawned), never moving by itself. `players` names some (all the seeded otherwise); `on: false`
 * takes every stand-in away, as a reset does. A real connection of one takes it over.
 */
export const devSetStandIns = defineEndpoint({
  method: 'POST',
  path: `${DEV_PREFIX}/stand-ins`,
  access: 'dev',
  body: z.object({
    on: z.boolean(),
    players: z.array(PlayerIdSchema).max(64).optional(),
  }),
  response: z.object({
    /** The characters standing in now. */
    standing: z.array(PlayerIdSchema),
  }),
});

export const devSetGod = defineEndpoint({
  method: 'POST',
  path: `${DEV_PREFIX}/god`,
  access: 'dev',
  body: z.object({
    playerId: PlayerIdSchema,
    on: z.boolean(),
    speed: z.number().int().min(1).max(GOD_MAX_SPEED).default(1),
    /** Each of its blows deals 9999 (B131): to clear or test a place fast. */
    fullDamage: z.boolean().default(false),
    /** Nothing costs it stamina (B131): attacks, dodges, guards. */
    freeStamina: z.boolean().default(false),
    /** Nothing is hidden from it (B131): walls, night, fog; to review a place at any hour. */
    seeAll: z.boolean().default(false),
    /**
     * Creatures still perceive it (B131, a scenario watched): no damage nor collision nor cost,
     * but what comes out for the character comes. Off, nothing perceives it.
     */
    perceived: z.boolean().default(false),
  }),
  response: z.object({
    /** Whether the character was found in the world. */
    found: z.boolean(),
    on: z.boolean(),
    speed: z.number().int().min(1).max(GOD_MAX_SPEED),
    fullDamage: z.boolean(),
    freeStamina: z.boolean(),
    seeAll: z.boolean(),
    perceived: z.boolean(),
    maxSpeed: z.number().int().positive(),
  }),
});
